// Программа «HRDeva» для Windows (desktop/hrdeva-calls; до октября 2026 —
// «HRDeva Звонки», отсюда имена файлов и адрес установщика) — маленькое
// приложение в трее. Оно держит эту страницу (/tray) в скрытом окне WebView2
// всё время, пока компьютер включён, и выдвигает окно у правого нижнего угла
// экрана, когда на линию звонят. Слушать и перехватывать звонок — та же
// всплывашка и то же окно звонка, что в newats (InboundCallHost): сама
// программа звонков не знает, она только окно, трей и обновления. Остальные
// её окно — обычное, тоже страница под /tray/: /tray/app, одно на «Звонки»,
// «Интервью» и «Инструменты» (tray/AppPage.tsx).
//
// Здесь — переписка страницы с программой: postMessage WebView2 в обе стороны.
// Без React: Gate в main.tsx сообщает программе, вошёл ли пользователь, ещё до
// роутера.

import {
  CAMERA_LOST_WHYS,
  CAMERA_WHYS,
  CALL_MODE,
  type CameraCall,
  type CameraLostWhy,
  type CameraMatrix,
  type CameraRing,
  type CameraWhy,
  type HostCameraInfo,
} from "@/pages/Interviews/room/camera/cameraHost";
import type { TrainingFailReason } from "@/pages/Interviews/room/training/trainingHost";

export const TRAY_PATH = "/tray";

/** Окно «Транскрибировать файл…» из меню значка: тот же инструмент, что во
 *  вкладке «Инструменты», но звук из файла программа вытаскивает сама, на
 *  компьютере (tray/TranscribePage.tsx, desktop/hrdeva-calls/README.md). */
export const TRANSCRIBE_PATH = "/tray/transcribe";

/** Окно «Инструменты» из меню значка: расшифровка и работа с видео (склейка,
 *  сжатие, обрезка) в одном окне с переключателем (tray/TrayToolsPage.tsx).
 *  Какой инструмент открыть, программа говорит якорем адреса — `#transcribe`,
 *  `#concat`, `#compress`, `#trim` (tray/videoFlow.ts, toolFromLocation):
 *  переход по якорю страницу не перезагружает, и идущая работа не теряется.
 *  Старые версии программы этого окна не знают и открывают TRANSCRIBE_PATH. */
export const TOOLS_PATH = "/tray/tools";

/** Окно «Интервью» из меню значка: экран «Интервью» сайта без шелла — встречи
 *  рекрутёра, ящик встречи и видеокабинет в этом же окне
 *  (tray/TrayInterviewsPage.tsx). Камеру, микрофон и показ экрана этому окну
 *  программа разрешает сама, без вопроса браузера. */
export const INTERVIEWS_PATH = "/tray/interviews";

/** Окно программы: одно на «Звонки», «Интервью» и «Инструменты»
 *  (tray/AppPage.tsx). Программа открывает его один раз и дальше только шлёт
 *  сообщение `show` — страница не перезагружается, и работа в других разделах
 *  не теряется. Какой раздел показать при первой загрузке, говорит адрес:
 *  `?section=calls|interviews|tools`, для «Интервью» ещё `&meeting=<uuid>` и
 *  `&join=1` (спросить, начать ли встречу) или `&oncall=1` (начать просили
 *  посреди звонка), для «Инструментов» якорь `#transcribe|concat|compress|trim`.
 *  Старые окна (/tray/tools, /tray/interviews, /tray/transcribe) работают как
 *  работали: их по-прежнему открывают установленные версии 1.0.5–1.0.7. */
export const APP_PATH = "/tray/app";

/** Откуда страница забирает сжатую дорожку: путь на этом же сайте, запрос по
 *  нему до сети не доходит — на него отвечает сама программа. Поэтому обычный
 *  fetch страницы работает без CORS. Зеркало `AudioFeed.Prefix`. */
export const AUDIO_PREFIX = "/__calls-app/audio/";

/** Установщик программы. Его и обновления кладёт на сайт CI (deploy:calls-app),
 *  установленная программа обновляется оттуда же сама. */
export const TRAY_SETUP_URL = "/calls-app/HRDevaCalls-win-Setup.exe";

/** Ширина окна программы в CSS-пикселях — у всплывашек, окна звонка и панели
 *  состояния одна; высоту страница сообщает сама, по содержимому. */
export const TRAY_WIDTH = 400;

export function isTrayPath(pathname: string): boolean {
  return pathname === TRAY_PATH || pathname.startsWith(TRAY_PATH + "/");
}

export function isTranscribePath(pathname: string): boolean {
  return pathname === TRANSCRIBE_PATH || pathname === TRANSCRIBE_PATH + "/";
}

export function isToolsPath(pathname: string): boolean {
  return pathname === TOOLS_PATH || pathname === TOOLS_PATH + "/";
}

/** Обычное окно программы с инструментом (не всплывашка звонка): расшифровка
 *  или «Инструменты». Страница в нём — всё окно, и ход работы показывает сама. */
export function isToolWindowPath(pathname: string): boolean {
  return isTranscribePath(pathname) || isToolsPath(pathname);
}

export function isInterviewsPath(pathname: string): boolean {
  return pathname === INTERVIEWS_PATH || pathname === INTERVIEWS_PATH + "/";
}

export function isAppPath(pathname: string): boolean {
  return pathname === APP_PATH || pathname === APP_PATH + "/";
}

type MessageFn = (e: { data: unknown }) => void;

/** Событие `sharedbufferreceived`: программа отдала странице кусок общей памяти
 *  (PostSharedBufferToScript). `additionalData` — то, что программа приложила
 *  JSON'ом (WebView2 разбирает его в объект). */
interface SharedBufferEvent {
  additionalData?: unknown;
  getBuffer(): ArrayBuffer;
}
type SharedBufferFn = (e: SharedBufferEvent) => void;

interface WebViewBridge {
  postMessage(msg: unknown): void;
  /** Сообщение вместе с объектами браузера (File). Появилось в WebView2 позже
   *  самой программы, поэтому необязательный. */
  postMessageWithAdditionalObjects?(msg: unknown, objects: readonly unknown[]): void;
  /** Общая память (WebView2 1.0.1054+): есть — программа может отдать кольцо кадров. */
  releaseBuffer?(buffer: ArrayBuffer): void;
  addEventListener(type: "message", fn: MessageFn): void;
  addEventListener(type: "sharedbufferreceived", fn: SharedBufferFn): void;
  removeEventListener(type: "message", fn: MessageFn): void;
  removeEventListener(type: "sharedbufferreceived", fn: SharedBufferFn): void;
}

function webview(): WebViewBridge | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { chrome?: { webview?: WebViewBridge } };
  return w.chrome?.webview ?? null;
}

/** Страница открыта внутри программы (в браузере `chrome.webview` нет). */
export function inTrayHost(): boolean {
  return webview() !== null;
}

// ---- страница → программа ----

export type AuthStatus = "authed" | "anon" | "unreachable";

/** Что показать в окне программы.
 *   hidden — ничего, окно спрятано;
 *   calls  — всплывашки и окно звонка: выдвинуть у угла экрана, поверх всех
 *            окон, не забирая фокус у того, в чём человек сейчас печатает;
 *   status — панель «программа работает» по клику на значок в трее. */
export type TrayMode = "hidden" | "calls" | "status";

export type ToHost =
  // Страница окна смонтировалась и слушает: пришлите настройки. Кусок
  // страницы грузится уже после входа, и то, что программа прислала раньше
  // (при загрузке, на auth), до неё могло не дойти.
  | { type: "ready" }
  | { type: "auth"; status: AuthStatus; user?: string }
  | { type: "view"; mode: TrayMode; width: number; height: number }
  // Ссылку — в браузер по умолчанию: карточку кандидата смотрят в newats.
  | { type: "open-url"; url: string }
  // Окно расшифровки. compress идёт только через postFileToHost: сам файл —
  // в additionalObjects, путь строкой программа не примет (страница назвала бы
  // любой файл на диске).
  | { type: "compress"; id: string }
  | { type: "cancel-compress"; id: string }
  // Дорожку по этому адресу забрали — программа удаляет временный файл.
  | { type: "taken"; path: string }
  // Показать в проводнике скачанную расшифровку.
  | { type: "reveal-download" }
  // Видео-инструменты окна «Инструменты»: всё делает ffmpeg программы на этом
  // компьютере, на сервер ничего не уходит. video-probe и video-run идут
  // только через postFilesToHost — сами файлы в additionalObjects, в том
  // порядке, в каком их склеивать; пути строкой нет ни в одну сторону.
  // Что за файлы: длительность, размер кадра, есть ли видео и звук.
  | { type: "video-probe"; id: string }
  // Запустить работу. preset — у сжатия; start_s, end_s и mode — у обрезки.
  | {
      type: "video-run";
      id: string;
      op: VideoOp;
      preset?: VideoPreset;
      start_s?: number;
      end_s?: number;
      mode?: TrimMode;
    }
  | { type: "video-cancel"; id: string }
  // Показать готовый файл в проводнике: путь программа помнит по id работы.
  | { type: "video-reveal"; id: string }
  // Окно «Интервью»: идёт ли встреча — рекрутёр в видеокабинете. Пока идёт,
  // программа не обновляется и, прежде чем закрыть окно, переспрашивает.
  | { type: "meeting-state"; active: boolean }
  // Окно программы (APP_PATH). Раздел «Звонки» звонками не занимается:
  // софтфон живёт на странице-всплывашке (/tray) — одна регистрация на
  // программу, — а окно только показывает его состояние и управляет программой.
  // Тумблер «Звук входящего» или «Запускать вместе с Windows»: программа
  // сохранит, обновит меню значка и пришлёт app-state заново.
  | { type: "set-setting"; name: AppSetting; on: boolean }
  // Запись встречи «для обучения нейросетей» (trainingBridge.ts): камеру и голос
  // пишет страница кабинета, программа кладёт куски на диск. data — base64;
  // seq идёт с нуля подряд у каждой дорожки; meta — JSON-объект описания.
  | { type: "training-start"; id: string; part: "recruiter" | "candidate"; mime: string; meetingId?: string }
  | { type: "training-chunk"; id: string; part: "recruiter" | "candidate"; seq: number; data: string }
  | { type: "training-meta"; id: string; meta: unknown }
  | { type: "training-stop"; id: string; meta: unknown }
  // «Показать записи»: папка последней записи в проводнике.
  | { type: "training-reveal" }
  // Нативная камера (tray/cameraBridge.ts): камеру кабинета открывает сама
  // программа и отдаёт кадры звонку через общую память. id — номер запроса
  // страницы; по нему же приходят ответы. camera-probe — «можешь быть владельцем
  // камеры?».
  | { type: "camera-probe"; id: string }
  | { type: "camera-open"; id: string; deviceId: string | null; width: number; height: number; fps: number }
  // О какой встрече речь (описание записи). Можно до camera-open и после.
  | {
      type: "camera-context";
      id: string;
      meeting: { meetingId: string; candidateId?: string; vacancyId?: string; recruiterId?: string };
      consent: { recordingGrantedAt?: string; source?: "ats" | "room_signal" | "in_person" };
    }
  // Закрыть и освободить камеру совсем (лампочка гаснет).
  | { type: "camera-close"; id: string }
  // Как звонку со своей камерой (раз в 5 с, пока камера открыта): по этому программа
  // снижает свою работу, звонок — никогда.
  | { type: "camera-health"; id: string; limit: "none" | "cpu" | "bandwidth" | "other"; longTaskMs: number }
  // «Показать пример»: программа передаёт его странице-всплывашке (demo).
  | { type: "run-demo" }
  | { type: "check-updates" }
  // «Перезапустить» на плашке обновления. Идёт через программу: при звонке,
  // живой встрече или идущей работе она переспросит.
  | { type: "restart-now" }
  // Занят ли раздел «Инструменты»: идёт расшифровка или работа над видео, или
  // её итог ещё на экране. Пока занят — программа не обновляется. working —
  // идёт сама работа, а не её итог на экране: прежде чем перезапуститься или
  // закрыться, программа о ней переспросит (отправку файла на расшифровку
  // ведёт страница, и узнать о ней программе больше неоткуда).
  | { type: "tools-state"; busy: boolean; working: boolean }
  // От страницы-всплывашки: есть ли звонок (свой или на прослушке) и сколько
  // их звонит. Пока звонок есть — программа не обновляется.
  | { type: "call-state"; active: boolean; ringing: number };

/** Настройки программы, которые окно переключает само. */
export type AppSetting = "sound" | "autostart" | "training-recording" | "native-camera";

/** Что сделать с видео. */
export type VideoOp = "concat" | "compress" | "trim";

/** Готовые настройки сжатия; сами числа (CRF, битрейт, высота кадра) — одна
 *  таблица в программе, странице известны только имена. */
export type VideoPreset = "messenger" | "balanced" | "smallest";

/** Обрезка: exact — кадр в кадр, с перекодированием; fast — копией потоков, рез
 *  по ближайшему ключевому кадру. */
export type TrimMode = "exact" | "fast";

export function postToHost(msg: ToHost): void {
  webview()?.postMessage(msg);
}

/** Программа умеет вытащить звук из файла (она же и WebView2 нужной версии). */
export function canCompress(): boolean {
  return typeof webview()?.postMessageWithAdditionalObjects === "function";
}

/** Отдать файл программе. false — не вышло, файл придётся грузить как есть. */
export function postFileToHost(msg: ToHost, file: File): boolean {
  return postFilesToHost(msg, [file]);
}

/** Отдать программе несколько файлов одним сообщением, по порядку списка.
 *  false — не вышло: программа старая или среди файлов есть такой, которого
 *  нет на диске (картинка из буфера обмена, вложение, перетащенное прямо из
 *  письма или архива). На такой WebView2 отвечает исключением, и сообщение не
 *  уходит целиком: пути у файла нет, программе брать нечего. */
export function postFilesToHost(msg: ToHost, files: readonly File[]): boolean {
  const wv = webview();
  if (typeof wv?.postMessageWithAdditionalObjects !== "function") return false;
  try {
    wv.postMessageWithAdditionalObjects(msg, files);
  } catch {
    return false;
  }
  return true;
}

// ---- программа → страница ----

export type FromHost =
  // version — версия самой программы (показывает панель состояния).
  | { type: "settings"; sound: boolean; version: string }
  // Клик по значку в трее.
  | { type: "show-status" }
  // Окно потеряло фокус: панель состояния прячется, как меню.
  | { type: "deactivated" }
  // «Показать пример звонка» в меню — проверить окно и звук.
  | { type: "demo" }
  // «Выйти из аккаунта» в меню.
  | { type: "logout" }
  // Окно расшифровки: ход сжатия. percent === null — пока неизвестно
  // (ffmpeg ещё не сказал длительность файла).
  | { type: "compress-progress"; id: string; percent: number | null }
  // Дорожка готова: путь, по которому её забрать (AUDIO_PREFIX), и размер.
  | { type: "compress-done"; id: string; path: string; bytes: number }
  | { type: "compress-failed"; id: string; reason: CompressFailReason }
  | { type: "compress-cancelled"; id: string }
  // Скачанная из окна расшифровка сохранена.
  | { type: "download-saved"; name: string; folder: string }
  // Запись для обучения остановилась: тумблер выключен, нет места, файл не
  // пишется. Кабинет закрывает свою запись и говорит рекрутёру.
  | { type: "training-error"; id: string; reason: TrainingFailReason }
  // Видео-инструменты. Ответ на video-probe: файлы в том же порядке, в каком
  // ушли. concat_copy — все подошли друг к другу и склеятся без
  // перекодирования; null — программа не сказала.
  | { type: "video-info"; id: string; files: VideoFileInfo[]; concat_copy: boolean | null }
  // percent === null — пока неизвестно.
  | { type: "video-progress"; id: string; percent: number | null }
  // Готово: имя и размер результата, размер исходников (сжатие показывает,
  // насколько стало меньше) и подпись папки. Подпись — только показать: пути
  // страница не знает, проводник открывает программа (video-reveal).
  | { type: "video-done"; id: string; name: string; size: number; source_size: number; folder: string }
  // Не вышло — и проверка (video-probe), и сама работа (video-run).
  | { type: "video-failed"; id: string; reason: VideoFailReason }
  | { type: "video-cancelled"; id: string }
  // Окно программы (APP_PATH). Всё, что раздел «Звонки» показывает: настройки,
  // версия, связь с сервером, звонки и готовое обновление. Приходит на ready и
  // при любой перемене.
  | ({ type: "app-state" } & AppState)
  // Перейти к разделу, не перезагружая страницу: значок в трее, ярлык, ссылка
  // hrdeva:// с сайта. meeting — карточка этой встречи в «Интервью»; join —
  // и начать её, но не сразу: ссылку мог нажать кто угодно, и страница сначала
  // спросит; oncall — начать просили, а идёт звонок: только сказать, что
  // встречу можно начать после него. Всё проверяется здесь же.
  | { type: "show"; section: AppSection; tool: AppTool | null; meeting: string | null; join: boolean; oncall: boolean }
  // Вход на странице-всплывашке (или в окне) изменился: переспросить /me.
  | { type: "recheck-auth" }
  // Нативная камера. Ответ на camera-probe: ok === false — страница открывает
  // камеру сама, why — почему; devices — камеры программы, call — что она готова
  // отдать звонку.
  | ({ type: "camera-probe-result"; id: string; v: number; devices: HostCameraInfo[]; call: CameraCall } & (
      | { ok: true }
      | { ok: false; why: CameraWhy }
    ))
  // Камера открыта; кольцо кадров придёт следом событием sharedbufferreceived.
  | {
      type: "camera-ready";
      id: string;
      deviceId: string;
      label: string;
      width: number;
      height: number;
      fps: number;
      format: "NV12";
      matrix: CameraMatrix;
      ring: CameraRing;
    }
  | { type: "camera-failed"; id: string; why: CameraWhy }
  // Программа закрыла камеру сама. К этому моменту камера уже свободна.
  | { type: "camera-lost"; id: string; why: CameraLostWhy }
  | { type: "camera-devices"; devices: HostCameraInfo[] }
  // Запись для обучения на стороне программы началась (on) или закончилась.
  | { type: "camera-rec"; id: string; on: boolean; segment: number; why?: string };

/** Разделы окна программы, в порядке переключателя. */
export type AppSection = "calls" | "interviews" | "tools";

export const APP_SECTIONS: readonly AppSection[] = ["calls", "interviews", "tools"];

/** Инструменты раздела «Инструменты» (якорь `#…` адреса окна). */
export type AppTool = "transcribe" | "concat" | "compress" | "trim";

/** Связь программы с сервером: ok — вошли и ответ есть; anon — вход не выполнен
 *  или истёк; offline — сервер не отвечает. */
export type AppLink = "ok" | "anon" | "offline";

/** Отправка записей для обучения на сервер (программа, Core: UploadStatus):
 *  idle — отправлять нечего; uploading — идёт; paused — уступила встрече или
 *  звонку; sign-in — ждёт входа; waiting — нет связи, повтор позже. */
export type TrainingUploadState = "idle" | "uploading" | "paused" | "sign-in" | "waiting";

export interface TrainingUpload {
  state: TrainingUploadState;
  /** Сколько файлов и байт ещё ждут отправки. */
  files: number;
  bytesLeft: number;
}

export interface AppState {
  sound: boolean;
  autostart: boolean;
  /** «Локальная запись для обучения нейросетей». */
  trainingRecording: boolean;
  /** «Нативная запись камеры (тест)»: камеру кабинета открывает сама программа.
   *  Старая программа поля не шлёт — тогда null («не умеет»): её переключатель молча
   *  отбросил бы сообщение, а страница показывала бы «включено» до следующего app-state. */
  nativeCamera: boolean | null;
  version: string;
  user: string | null;
  link: AppLink;
  /** Звонок идёт / сколько звонят сейчас. */
  call: { active: boolean; ringing: number };
  /** Готовая к установке версия; null — обновления нет. */
  update: string | null;
  /** Отправка записей для обучения; null — программа старая и о ней не говорит. */
  trainingUpload: TrainingUpload | null;
}

/** Почему программа не смогла вытащить звук. Зеркало `FfmpegReasons`. */
export type CompressFailReason = "unknown-format" | "no-audio" | "no-ffmpeg" | "busy" | "other";

const FAIL_REASONS: readonly CompressFailReason[] = [
  "unknown-format",
  "no-audio",
  "no-ffmpeg",
  "busy",
  "other",
];

function failReason(raw: unknown): CompressFailReason {
  return FAIL_REASONS.includes(raw as CompressFailReason) ? (raw as CompressFailReason) : "other";
}

/** Почему не вышло с видео — одна таблица на обе стороны (фразы для человека —
 *  tray/videoFlow.ts, videoFailText):
 *   not-video — в файле нет видео или он не читается;
 *   no-ffmpeg — рядом с программой нет ffmpeg.exe;
 *   busy      — программа уже обрабатывает другое видео;
 *   write     — не удалось записать результат в папку;
 *   concat    — файлы не удалось склеить;
 *   range     — начало и конец обрезки не подходят к файлу;
 *   other     — всё остальное. */
export type VideoFailReason = "not-video" | "no-ffmpeg" | "busy" | "write" | "concat" | "range" | "other";

export const VIDEO_FAIL_REASONS: readonly VideoFailReason[] = [
  "not-video",
  "no-ffmpeg",
  "busy",
  "write",
  "concat",
  "range",
  "other",
];

function videoFailReason(raw: unknown): VideoFailReason {
  return VIDEO_FAIL_REASONS.includes(raw as VideoFailReason) ? (raw as VideoFailReason) : "other";
}

/** Что программа узнала о файле (ffmpeg -i). ok === false — использовать
 *  нельзя: не видео или не читается, reason говорит почему. */
export interface VideoFileInfo {
  name: string;
  ok: boolean;
  duration_s: number;
  width: number;
  height: number;
  has_video: boolean;
  has_audio: boolean;
  reason: VideoFailReason | null;
}

function nonNegative(raw: unknown): number {
  return typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? raw : 0;
}

function percentOf(raw: unknown): number | null {
  return typeof raw === "number" && Number.isFinite(raw) ? Math.min(100, Math.max(0, Math.round(raw))) : null;
}

function videoFileInfo(raw: unknown): VideoFileInfo {
  const f = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  // Годным файл считается, только если программа сказала это прямо: молчание
  // и мусор — «не подошёл», иначе в работу ушло бы то, что не открылось.
  const ok = f.ok === true && f.has_video === true;
  return {
    name: typeof f.name === "string" ? f.name : "",
    ok,
    duration_s: nonNegative(f.duration_s),
    width: Math.round(nonNegative(f.width)),
    height: Math.round(nonNegative(f.height)),
    has_video: f.has_video === true,
    has_audio: f.has_audio === true,
    reason: ok ? null : videoFailReason(f.reason ?? "not-video"),
  };
}

const TOOL_IDS: readonly AppTool[] = ["transcribe", "concat", "compress", "trim"];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isMeetingId(raw: unknown): raw is string {
  return typeof raw === "string" && UUID_RE.test(raw);
}

const TRAINING_FAILS: readonly TrainingFailReason[] = ["disabled", "no-space", "write", "order", "request"];

function isTrainingFail(raw: unknown): raw is TrainingFailReason {
  return typeof raw === "string" && TRAINING_FAILS.includes(raw as TrainingFailReason);
}

const UPLOAD_STATES: readonly TrainingUploadState[] = ["idle", "uploading", "paused", "sign-in", "waiting"];

function parseUpload(raw: unknown): TrainingUpload | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const u = raw as Record<string, unknown>;
  if (!UPLOAD_STATES.includes(u.state as TrainingUploadState)) return null;
  return { state: u.state as TrainingUploadState, files: count(u.files), bytesLeft: count(u.bytes_left) };
}

function count(raw: unknown): number {
  return typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0;
}

// ---- нативная камера: разбор ответов программы ----

function posInt(raw: unknown): number | null {
  return typeof raw === "number" && Number.isSafeInteger(raw) && raw > 0 ? raw : null;
}

function cameraWhy(raw: unknown): CameraWhy {
  return CAMERA_WHYS.includes(raw as CameraWhy) ? (raw as CameraWhy) : "error";
}

function cameraLostWhy(raw: unknown): CameraLostWhy {
  return CAMERA_LOST_WHYS.includes(raw as CameraLostWhy) ? (raw as CameraLostWhy) : "error";
}

/** Камеры программы: без id камера не выбрать, без имени — подписать «Камера». */
function hostCameras(raw: unknown): HostCameraInfo[] {
  if (!Array.isArray(raw)) return [];
  const out: HostCameraInfo[] = [];
  for (const d of raw) {
    if (!d || typeof d !== "object" || Array.isArray(d)) continue;
    const o = d as Record<string, unknown>;
    if (typeof o.id !== "string" || !o.id) continue;
    out.push({ id: o.id, name: typeof o.name === "string" && o.name.trim() ? o.name.trim() : "Камера" });
  }
  return out;
}

/** Режим звонка: чего нет в ответе, берём из контракта (1280×720 при 30). */
function cameraCall(raw: unknown): CameraCall {
  const c = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  return {
    width: posInt(c.width) ?? CALL_MODE.width,
    height: posInt(c.height) ?? CALL_MODE.height,
    fps: posInt(c.fps) ?? CALL_MODE.fps,
  };
}

function parseProbeResult(m: Record<string, unknown>): FromHost | null {
  if (typeof m.id !== "string") return null;
  const base = { id: m.id, v: count(m.v), devices: hostCameras(m.devices), call: cameraCall(m.call) };
  // Годной программа считается, только если сказала это прямо.
  return m.ok === true
    ? { type: "camera-probe-result", ok: true, ...base }
    : { type: "camera-probe-result", ok: false, why: cameraWhy(m.why), ...base };
}

/** `camera-ready` берём только целиком и только NV12: чужой формат или дырявая
 *  раскладка кольца — сообщение мимо, и страница, не дождавшись, идёт прежним путём. */
function parseCameraReady(m: Record<string, unknown>): FromHost | null {
  const r = m.ring && typeof m.ring === "object" && !Array.isArray(m.ring) ? (m.ring as Record<string, unknown>) : null;
  const width = posInt(m.width);
  const height = posInt(m.height);
  const fps = posInt(m.fps);
  const slots = r ? posInt(r.slots) : null;
  const stride = r ? posInt(r.stride) : null;
  const ctrl = r ? posInt(r.ctrl) : null;
  const frameBytes = r ? posInt(r.frameBytes) : null;
  if (typeof m.id !== "string" || m.format !== "NV12") return null;
  if (!width || !height || !fps || !slots || !stride || !ctrl || !frameBytes) return null;
  return {
    type: "camera-ready",
    id: m.id,
    deviceId: typeof m.deviceId === "string" ? m.deviceId : "",
    label: typeof m.label === "string" ? m.label : "",
    width,
    height,
    fps,
    format: "NV12",
    matrix: m.matrix === "smpte170m" ? "smpte170m" : "bt709",
    ring: { slots, stride, ctrl, frameBytes },
  };
}

/** Страница новее программы, а не наоборот (так выкатывается сайт), поэтому
 *  недостающее поле — не ошибка: звук включён, автозапуск выключен, связь в
 *  порядке (страница сама открыта — вход есть), звонков и обновления нет. */
function parseAppState(m: Record<string, unknown>): FromHost {
  const call = m.call && typeof m.call === "object" && !Array.isArray(m.call) ? (m.call as Record<string, unknown>) : {};
  const link = m.link === "anon" || m.link === "offline" ? m.link : "ok";
  return {
    type: "app-state",
    sound: m.sound !== false,
    autostart: m.autostart === true,
    trainingRecording: m.trainingRecording === true,
    nativeCamera: typeof m.nativeCamera === "boolean" ? m.nativeCamera : null,
    version: typeof m.version === "string" ? m.version : "",
    user: typeof m.user === "string" && m.user.trim() ? m.user.trim() : null,
    link,
    call: { active: call.active === true, ringing: count(call.ringing) },
    update: typeof m.update === "string" && m.update.trim() ? m.update.trim() : null,
    trainingUpload: parseUpload(m.trainingUpload),
  };
}

/** `show` проверяется целиком: раздел — из известных, инструмент — из
 *  известных, встреча — настоящий uuid. Незнакомый раздел — сообщение мимо;
 *  остальное лишнее отбрасывается (join без встречи — не join, oncall рядом с
 *  join — не oncall). */
function parseShow(m: Record<string, unknown>): FromHost | null {
  if (!APP_SECTIONS.includes(m.section as AppSection)) return null;
  const section = m.section as AppSection;
  const tool = section === "tools" && TOOL_IDS.includes(m.tool as AppTool) ? (m.tool as AppTool) : null;
  const meeting = section === "interviews" && isMeetingId(m.meeting) ? m.meeting.toLowerCase() : null;
  const join = meeting !== null && m.join === true;
  return { type: "show", section, tool, meeting, join, oncall: meeting !== null && !join && m.oncall === true };
}

/** Разобрать сообщение программы; чужое и битое — null. */
export function parseHostMessage(data: unknown): FromHost | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const m = data as Record<string, unknown>;
  switch (m.type) {
    case "settings":
      return {
        type: "settings",
        sound: m.sound !== false,
        version: typeof m.version === "string" ? m.version : "",
      };
    case "training-error":
      return typeof m.id === "string" && isTrainingFail(m.reason) ? { type: "training-error", id: m.id, reason: m.reason } : null;
    case "show-status":
    case "deactivated":
    case "demo":
    case "logout":
      return { type: m.type };
    case "compress-progress":
      if (typeof m.id !== "string") return null;
      return { type: "compress-progress", id: m.id, percent: percentOf(m.percent) };
    case "compress-done":
      // Адрес — только свой путь того же сайта: его страница сейчас запросит
      // через fetch, и брать его откуда попало незачем.
      if (typeof m.id !== "string" || typeof m.path !== "string" || !m.path.startsWith(AUDIO_PREFIX)) {
        return null;
      }
      return {
        type: "compress-done",
        id: m.id,
        path: m.path,
        bytes: typeof m.bytes === "number" && m.bytes > 0 ? m.bytes : 0,
      };
    case "compress-failed":
      return typeof m.id === "string"
        ? { type: "compress-failed", id: m.id, reason: failReason(m.reason) }
        : null;
    case "compress-cancelled":
      return typeof m.id === "string" ? { type: "compress-cancelled", id: m.id } : null;
    case "download-saved":
      return {
        type: "download-saved",
        name: typeof m.name === "string" ? m.name : "",
        folder: typeof m.folder === "string" ? m.folder : "",
      };
    case "video-info":
      // Без списка файлов ответ ни о чём: страница не узнала бы, что подошло.
      if (typeof m.id !== "string" || !Array.isArray(m.files)) return null;
      return {
        type: "video-info",
        id: m.id,
        files: m.files.map(videoFileInfo),
        concat_copy: typeof m.concat_copy === "boolean" ? m.concat_copy : null,
      };
    case "video-progress":
      return typeof m.id === "string" ? { type: "video-progress", id: m.id, percent: percentOf(m.percent) } : null;
    case "video-done":
      if (typeof m.id !== "string") return null;
      return {
        type: "video-done",
        id: m.id,
        name: typeof m.name === "string" ? m.name : "",
        size: nonNegative(m.size),
        source_size: nonNegative(m.source_size),
        folder: typeof m.folder === "string" ? m.folder : "",
      };
    case "video-failed":
      return typeof m.id === "string" ? { type: "video-failed", id: m.id, reason: videoFailReason(m.reason) } : null;
    case "video-cancelled":
      return typeof m.id === "string" ? { type: "video-cancelled", id: m.id } : null;
    case "app-state":
      return parseAppState(m);
    case "show":
      return parseShow(m);
    case "recheck-auth":
      return { type: "recheck-auth" };
    case "camera-probe-result":
      return parseProbeResult(m);
    case "camera-ready":
      return parseCameraReady(m);
    case "camera-failed":
      return typeof m.id === "string" ? { type: "camera-failed", id: m.id, why: cameraWhy(m.why) } : null;
    case "camera-lost":
      return typeof m.id === "string" ? { type: "camera-lost", id: m.id, why: cameraLostWhy(m.why) } : null;
    case "camera-devices":
      return { type: "camera-devices", devices: hostCameras(m.devices) };
    case "camera-rec":
      if (typeof m.id !== "string") return null;
      return {
        type: "camera-rec",
        id: m.id,
        on: m.on === true,
        segment: count(m.segment),
        ...(typeof m.why === "string" && m.why ? { why: m.why } : {}),
      };
    default:
      return null;
  }
}

/** Подписка на сообщения программы. Вне программы — пустая отписка. */
export function onHostMessage(fn: (msg: FromHost) => void): () => void {
  const wv = webview();
  if (!wv) return () => {};
  const handler: MessageFn = (e) => {
    const msg = parseHostMessage(e.data);
    if (msg) fn(msg);
  };
  wv.addEventListener("message", handler);
  return () => wv.removeEventListener("message", handler);
}

// ---- общая память (нативная камера) ----

/** Умеет ли WebView2 этого компьютера отдавать странице общую память. */
export function sharedBuffersSupported(): boolean {
  return typeof webview()?.releaseBuffer === "function";
}

/** Кусок общей памяти от программы: `kind` и `id` она приложила сама, буфер берёт
 *  `getBuffer()` (только если кусок нужен — иначе его отпускают releaseSharedBuffer). */
export interface SharedBufferArrival {
  kind: string;
  id: string;
  getBuffer(): ArrayBuffer;
}

/** `additionalData` приходит объектом, но на всякий случай понимаем и строку JSON. */
export function parseSharedBufferData(raw: unknown): { kind: string; id: string } | null {
  let data = raw;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const d = data as Record<string, unknown>;
  return typeof d.kind === "string" && typeof d.id === "string" ? { kind: d.kind, id: d.id } : null;
}

/** Подписка на куски общей памяти от программы. Вне программы — пустая отписка. */
export function onSharedBuffer(fn: (arrival: SharedBufferArrival) => void): () => void {
  const wv = webview();
  if (!wv) return () => {};
  const handler: SharedBufferFn = (e) => {
    const data = parseSharedBufferData(e.additionalData);
    if (data) fn({ ...data, getBuffer: () => e.getBuffer() });
  };
  wv.addEventListener("sharedbufferreceived", handler);
  return () => wv.removeEventListener("sharedbufferreceived", handler);
}

/** Отпустить ненужный кусок общей памяти (его не взяли: запрос уже отменён). */
export function releaseSharedBuffer(buffer: ArrayBuffer): void {
  try {
    webview()?.releaseBuffer?.(buffer);
  } catch {
    // освободится вместе со страницей
  }
}
